import { isInFuture, toFormat } from "@/lib/dateHelpers";
import { TaskStageValues, TaskVisibilityValues } from "@/types";
import { isoDateDaysFromNow } from "@/features/global/components/AddTaskSideSheet/quickDueDate";
import type { BackofficeCRMCreateTaskInput } from "@/features/global/components/AddTaskSideSheet/mutations";

export const MEMBERSHIP_SERVICES_DEPARTMENT = "Membership Services";

export const NOTIFY_EMPLOYMENT_CHANGE_ACTION_LABEL = "Notify of Employment Change";

export const EMPLOYMENT_CHANGE_NOTICE_NO_END_DATE_LABEL = "Present";

export type EmploymentChangeNoticePerson = {
  firstName?: string | null;
  lastName?: string | null;
  displayName?: string | null;
};

export function employmentChangeNoticePersonName(person: EmploymentChangeNoticePerson): string {
  const displayName = person.displayName?.trim();
  if (displayName) return displayName;

  const parts = [person.firstName, person.lastName].map((value) => value?.trim()).filter(Boolean);
  return parts.join(" ") || "Unknown";
}

export function buildEmploymentCareerPageUrl(pgaId: string, origin: string = window.location.origin): string {
  return `${origin}/people/${pgaId}/career/employment`;
}

export function buildEmploymentChangeNoticeSubject(personName: string, pgaId: string): string {
  return `Employment end date for ${personName} (${pgaId})`;
}

export function buildEmploymentChangeNoticeBody({
  personName,
  pgaId,
  facilityName,
  endedOn,
  careerPageUrl
}: {
  personName: string;
  pgaId: string;
  facilityName: string;
  endedOn: string;
  careerPageUrl: string;
}): string {
  return [
    `Person Name: ${personName}`,
    `PGA ID: ${pgaId}`,
    `Facility Name: ${facilityName.trim() || "Unknown"}`,
    `End Date: ${toFormat(endedOn) ?? endedOn}`,
    `Career Page: ${careerPageUrl}`
  ].join("\n");
}

export function buildEmploymentChangeNoticeTaskInput({
  personName,
  pgaId,
  personId,
  facilityId,
  facilityName,
  endedOn,
  careerPageUrl,
  dueDate = isoDateDaysFromNow(7)
}: {
  personName: string;
  pgaId: string;
  personId: string;
  facilityId: string;
  facilityName: string;
  endedOn: string;
  careerPageUrl: string;
  dueDate?: string | null;
}): BackofficeCRMCreateTaskInput {
  return {
    name: buildEmploymentChangeNoticeSubject(personName, pgaId),
    description: buildEmploymentChangeNoticeBody({
      personName,
      pgaId,
      facilityName,
      endedOn,
      careerPageUrl
    }),
    teamTags: [MEMBERSHIP_SERVICES_DEPARTMENT],
    visibility: TaskVisibilityValues.PUBLIC,
    stage: TaskStageValues.OPEN,
    ...(dueDate ? { dueDate } : {}),
    references: [
      { referenceableId: personId, referenceableType: "PERSON" },
      { referenceableId: facilityId, referenceableType: "FACILITY" }
    ]
  };
}

export function isActiveMemberEmploymentRow(row: {
  endDate: string | null;
  startDateRaw?: string | null;
  employmentKind?: string;
}): boolean {
  const startedOn = row.startDateRaw;
  return (
    row.endDate === EMPLOYMENT_CHANGE_NOTICE_NO_END_DATE_LABEL &&
    !!startedOn &&
    !isInFuture(startedOn) &&
    row.employmentKind !== "NON_MEMBER"
  );
}
